import { useEffect, useState } from "react";
import { useDepositSettings } from "@/hooks/useDepositSettings";
import ClientAvatar from "@/components/ClientAvatar";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useAdmin } from "@/hooks/useAdmin";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import {
  CalendarDays, Clock, DollarSign, Shield, Gift, Copy, Check, Tag, Loader2,
  Camera, Instagram, User, Phone, Mail, Edit2, Save, X, ChevronDown, ChevronUp, RefreshCw,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import AddToCalendarButtons from "@/components/AddToCalendarButtons";
import ClientAddonsSection from "@/components/ClientAddonsSection";
// Photos managed via separate page
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

interface Booking {
  id: string;
  user_id: string;
  service_name: string;
  service_category: string;
  booking_date: string;
  booking_time: string;
  duration: string;
  price: number;
  status: string;
  payment_status?: string | null;
  deposit_paid_amount?: number | null;
  discount_applied?: number | null;
}

interface Profile {
  full_name: string | null;
  email: string | null;
  phone: string | null;
  instagram_link: string | null;
  referral_code: string | null;
  instagram_bypass_approval?: boolean | null;
}

const Account = () => {
  const { user, loading: authLoading, signOut } = useAuth();
  const { isAdmin } = useAdmin();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [referralCode, setReferralCode] = useState("");
  const [referralCount, setReferralCount] = useState(0);
  const [rewardTiers, setRewardTiers] = useState<{ referrals_needed: number; reward_description: string }[]>([]);
  const [rewardsEnabled, setRewardsEnabled] = useState(false);
  const [referralInput, setReferralInput] = useState("");
  const [referralSubmitting, setReferralSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [coupons, setCoupons] = useState<any[]>([]);
  const [igPosts, setIgPosts] = useState<{ id: string; post_url: string; status: string; created_at: string }[]>([]);
  const [igPostInput, setIgPostInput] = useState("");
  const [igPostSubmitting, setIgPostSubmitting] = useState(false);
  const [igAutoApprove, setIgAutoApprove] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editInstagram, setEditInstagram] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (!user) return;
    const fetchBookings = async () => {
      const { data } = await supabase
        .from("bookings")
        .select("*").is("deleted_at", null)
        .order("booking_date", { ascending: false });
      setBookings(data || []);
      setLoading(false);
    };

    const fetchProfile = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("full_name, email, phone, instagram_link, referral_code, instagram_bypass_approval")
        .eq("user_id", user.id)
        .maybeSingle();
      if (data) {
        setProfile(data as Profile);
        if (data.referral_code) setReferralCode(data.referral_code);
        setEditName(data.full_name || "");
        setEditPhone(data.phone || "");
        setEditInstagram(data.instagram_link || "");
      }

      const { data: entries } = await supabase
        .from("referral_entries")
        .select("id")
        .eq("referrer_user_id", user.id);
      setReferralCount(entries?.length || 0);

      const { data: settings } = await supabase
        .from("site_settings")
        .select("key, value")
        .in("key", ["reward_program_enabled", "reward_tiers", "instagram_auto_approve"]);
      settings?.forEach((s: any) => {
        if (s.key === "reward_program_enabled") setRewardsEnabled(s.value === "true");
        if (s.key === "reward_tiers") {
          try { setRewardTiers(JSON.parse(s.value)); } catch {}
        }
        if (s.key === "instagram_auto_approve") setIgAutoApprove(s.value === "true");
      });
    };

    const fetchCoupons = async () => {
      const { data } = await supabase
        .from("coupons")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      setCoupons(data || []);
    };

    const fetchIgPosts = async () => {
      const { data } = await supabase
        .from("instagram_posts")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      setIgPosts((data as any[]) || []);
    };

    fetchBookings();
    fetchProfile();
    fetchCoupons();
    fetchIgPosts();
  }, [user]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/auth" replace />;
  if (isAdmin) return <Navigate to="/admin" replace />;

  const handleSaveProfile = async () => {
    const trimmedName = editName.trim();
    if (trimmedName.length < 2) {
      toast.error("Please enter your full name (at least 2 characters)");
      return;
    }
    setSavingProfile(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        full_name: trimmedName,
        phone: editPhone.trim() || null,
        instagram_link: editInstagram.trim() || null,
      })
      .eq("user_id", user.id);
    if (error) {
      toast.error("Failed to update profile");
    } else {
      toast.success("Profile updated!");
      setProfile((prev) => prev ? {
        ...prev,
        full_name: trimmedName,
        phone: editPhone.trim() || null,
        instagram_link: editInstagram.trim() || null,
      } : prev);
      setEditingProfile(false);
    }
    setSavingProfile(false);
  };

  const getBookingStart = (b: Booking): Date => {
    // booking_time is e.g. "2:30 PM" or "14:30"
    const t = b.booking_time.trim();
    const ampm = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    let h = 0, m = 0;
    if (ampm) {
      h = parseInt(ampm[1], 10);
      m = parseInt(ampm[2], 10);
      const period = ampm[3].toUpperCase();
      if (period === "PM" && h !== 12) h += 12;
      if (period === "AM" && h === 12) h = 0;
    } else {
      const parts = t.split(":").map((n) => parseInt(n, 10));
      h = parts[0] || 0;
      m = parts[1] || 0;
    }
    const [yy, mo, dd] = b.booking_date.split("-").map((n) => parseInt(n, 10));
    return new Date(yy, (mo || 1) - 1, dd || 1, h, m, 0, 0);
  };

  const isWithin24h = (b: Booking): boolean => {
    return getBookingStart(b).getTime() - Date.now() < 24 * 60 * 60 * 1000;
  };

  const handleCancel = async (bookingId: string) => {
    const booking = bookings.find((b) => b.id === bookingId);
    if (booking && isWithin24h(booking)) {
      toast.error("Appointments cannot be cancelled within 24 hours of the start time.");
      return;
    }
    const { error } = await supabase
      .from("bookings")
      .update({ status: "cancelled" })
      .eq("id", bookingId);

    if (error) {
      toast.error("Failed to cancel. Please try again.");
    } else {
      toast.success("Appointment cancelled.");
      setBookings((prev) =>
        prev.map((b) => (b.id === bookingId ? { ...b, status: "cancelled" } : b))
      );

      if (booking && user?.email) {
        supabase.functions.invoke("send-booking-email", {
          body: {
            type: "cancellation",
            email: user.email,
            service_name: booking.service_name,
            service_category: booking.service_category,
            booking_date: booking.booking_date,
            booking_time: booking.booking_time,
            duration: booking.duration,
            price: booking.price,
            referral_code: referralCode || undefined,
          },
        }).catch((err) => console.error("Email send failed:", err));
      }

      if (booking) {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("phone")
          .eq("user_id", user.id)
          .maybeSingle();

        if (profileData?.phone) {
          supabase.functions.invoke("send-booking-sms", {
            body: {
              type: "cancellation",
              phone: profileData.phone,
              service_name: booking.service_name,
              booking_date: booking.booking_date,
              booking_time: booking.booking_time,
              duration: booking.duration,
              price: booking.price,
            },
          }).catch((err) => console.error("SMS send failed:", err));
        }
      }
    }
  };


  const handleChangeRequest = async (booking: Booking) => {
    if (isWithin24h(booking)) {
      toast.error("Appointments cannot be changed within 24 hours of the start time.");
      return;
    }

    // Notify admin via email that a reschedule is being attempted.
    // We do NOT cancel the original booking here — it stays confirmed until
    // the new pending booking is successfully created on the next page.
    supabase.functions.invoke("send-booking-email", {
      body: {
        type: "change_request",
        email: "Nijah@inparadisebeauty.com",
        client_name: profile?.full_name || user?.email || "A client",
        service_name: booking.service_name,
        service_category: booking.service_category,
        booking_date: booking.booking_date,
        booking_time: booking.booking_time,
        duration: booking.duration,
        price: booking.price,
      },
    }).catch((err) => console.error("Change request email failed:", err));

    toast.success("Redirecting to reschedule your appointment...");
    // Pre-populate the booking page with the same service for rescheduling.
    // originalBookingId tells Book.tsx to skip the deposit and create a
    // pending booking that an admin will confirm.
    const params = new URLSearchParams({
      service: booking.service_name,
      category: booking.service_category,
      reschedule: "true",
      originalBookingId: booking.id,
    });
    navigate(`/book?${params.toString()}`);
  };

  const upcoming = bookings.filter(
    (b) => new Date(b.booking_date + "T23:59:59") >= new Date() && (b.status === "confirmed" || b.status === "pending")
  );
  const past = bookings.filter(
    (b) => new Date(b.booking_date + "T23:59:59") < new Date() || (b.status !== "confirmed" && b.status !== "pending")
  );

  const activeCoupons = coupons.filter(
    (c) => !c.is_used && new Date(c.expires_at) >= new Date()
  );
  const approvedPosts = igPosts.filter((p) => p.status === "approved").length;
  const pendingPosts = igPosts.filter((p) => p.status === "pending").length;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="pt-32 pb-24">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="flex items-center justify-between mb-10">
            <div className="flex items-center gap-4">
              <ClientAvatar userId={user.id} name={profile?.full_name} size="xl" />
              <div>
                <p className="text-primary text-xs font-light tracking-[0.5em] uppercase mb-2">My Account</p>
                <h1 className="font-serif text-3xl md:text-4xl font-bold italic">
                  {profile?.full_name || user.email}
                </h1>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button onClick={signOut} variant="outline" className="rounded-none border-primary/30 hover:bg-primary/10 tracking-[0.2em] uppercase text-xs font-medium">
                Sign Out
              </Button>
              <Button asChild variant="ghost" className="rounded-none text-destructive hover:text-destructive hover:bg-destructive/10 tracking-[0.2em] uppercase text-xs font-medium">
                <Link to="/delete-account">Delete Account</Link>
              </Button>
            </div>
          </div>

          <div className="mb-8 flex flex-wrap gap-4">
            <Button asChild className="rounded-none px-10 py-5 text-xs font-medium tracking-[0.3em] uppercase border border-primary hover:bg-primary/90">
              <Link to="/book">Book an Appointment</Link>
            </Button>
            {isAdmin && (
              <Button asChild variant="outline" className="rounded-none px-10 py-5 text-xs font-medium tracking-[0.3em] uppercase border-primary/40 hover:bg-primary/10 hover:border-primary">
                <Link to="/admin"><Shield size={14} className="mr-2" /> Admin Panel</Link>
              </Button>
            )}
          </div>

          {/* Profile Summary Card */}
          <div className="mb-12 bg-card border border-border/50 p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <User size={20} className="text-primary" />
                <h2 className="font-serif text-xl font-bold italic">My Profile</h2>
              </div>
              {!editingProfile ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingProfile(true)}
                  className="text-xs gap-1.5 text-muted-foreground hover:text-foreground"
                >
                  <Edit2 size={14} /> Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditingProfile(false);
                      setEditName(profile?.full_name || "");
                      setEditPhone(profile?.phone || "");
                      setEditInstagram(profile?.instagram_link || "");
                    }}
                    className="text-xs gap-1.5 text-muted-foreground"
                  >
                    <X size={14} /> Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                    className="text-xs gap-1.5"
                  >
                    {savingProfile ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Save
                  </Button>
                </div>
              )}
            </div>

            {editingProfile ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Full Name</label>
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="bg-background border-border/50" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Phone</label>
                  <Input value={editPhone} onChange={(e) => setEditPhone(e.target.value)} className="bg-background border-border/50" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Instagram Handle / Link</label>
                  <Input value={editInstagram} onChange={(e) => setEditInstagram(e.target.value)} placeholder="@handle or https://instagram.com/..." className="bg-background border-border/50" />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-2.5 text-sm">
                  <User size={14} className="text-muted-foreground shrink-0" />
                  <span>{profile?.full_name || "—"}</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm">
                  <Mail size={14} className="text-muted-foreground shrink-0" />
                  <span>{profile?.email || user.email || "—"}</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm">
                  <Phone size={14} className="text-muted-foreground shrink-0" />
                  <span>{profile?.phone || "—"}</span>
                </div>
              <div className="flex items-center gap-2.5 text-sm">
                  <Instagram size={14} className="text-muted-foreground shrink-0" />
                  {profile?.instagram_link ? (
                    <a href={profile.instagram_link.startsWith("http") ? profile.instagram_link : `https://instagram.com/${profile.instagram_link.replace("@", "")}`} target="_blank" rel="noopener noreferrer" className="text-primary underline truncate">
                      {profile.instagram_link}
                    </a>
                  ) : (
                    <span className="text-muted-foreground">Not set</span>
                  )}
                </div>
                <Link to="/account#photos" className="flex items-center gap-2.5 text-sm text-primary hover:underline">
                  <Camera size={14} className="shrink-0" />
                  <span>My Photos</span>
                </Link>
              </div>
            )}

            {/* Referral Code - always visible */}
            {referralCode && (
              <div className="mt-4 pt-4 border-t border-border/30">
                <div className="flex items-center gap-3">
                  <Gift size={16} className="text-primary shrink-0" />
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-1">Your Referral Code</p>
                    <div className="flex items-center gap-2">
                      <code className="bg-primary/10 border border-primary/20 px-4 py-2 text-lg font-mono font-bold tracking-widest text-primary">{referralCode}</code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(referralCode);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        }}
                        className="text-xs gap-1"
                      >
                        {copied ? <Check size={14} className="text-primary" /> : <Copy size={14} />}
                        {copied ? "Copied!" : "Copy"}
                      </Button>
                    </div>
                    <div className="mt-2 p-3 border border-primary/20 bg-primary/5 rounded-sm">
                      <p className="text-sm font-medium text-primary mb-1">💰 Earn $5 for every referral!</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Share your referral code with friends. When they book and complete their first appointment, you automatically receive a <strong className="text-primary">$5 credit</strong> to use on your next service!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Combined Rewards, Coupons & Instagram Section */}
          <div className="mb-12 bg-card border border-border/50 p-6 space-y-5">
            <div className="flex items-center gap-2 mb-2">
              <Gift size={20} className="text-primary" />
              <h2 className="font-serif text-xl font-bold italic">Rewards & Coupons</h2>
            </div>

            {/* Dollar summary */}
            {(() => {
              const totalEarned = coupons.reduce((sum: number, c: any) => sum + Number(c.discount_amount), 0);
              const totalSpent = coupons.filter((c: any) => c.is_used).reduce((sum: number, c: any) => sum + Number(c.discount_amount), 0);
              const totalAvailable = activeCoupons.reduce((sum: number, c: any) => sum + Number(c.discount_amount), 0);
              return (
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 border border-border/30 bg-muted/20 text-center">
                    <p className="text-2xl font-bold text-primary">${totalEarned}</p>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Earned</p>
                  </div>
                  <div className="p-3 border border-border/30 bg-muted/20 text-center">
                    <p className="text-2xl font-bold text-foreground">${totalSpent}</p>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Used</p>
                  </div>
                  <div className="p-3 border border-primary/20 bg-primary/5 text-center">
                    <p className="text-2xl font-bold text-primary">${totalAvailable}</p>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Available</p>
                  </div>
                </div>
              );
            })()}

            {/* Expandable: Active Coupons */}
            <ExpandableSection
              icon={<Tag size={16} className="text-primary" />}
              title="Active Coupons"
              badge={activeCoupons.length > 0 ? String(activeCoupons.length) : undefined}
            >
              {activeCoupons.length === 0 ? (
                <p className="text-sm text-muted-foreground">No active coupons right now.</p>
              ) : (
                <div className="space-y-2">
                  {activeCoupons.map((coupon: any) => (
                    <div key={coupon.id} className="flex items-center justify-between p-3 border border-primary/30 bg-primary/5">
                      <div className="flex items-center gap-3">
                        <code className="bg-muted px-2 py-1 font-mono font-bold text-xs tracking-widest">{coupon.code}</code>
                        <span className="text-primary font-semibold text-sm">${coupon.discount_amount} off</span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        Expires {format(new Date(coupon.expires_at), "MMM d, yyyy")}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </ExpandableSection>

            {/* Expandable: Past Coupons */}
            {coupons.filter((c: any) => c.is_used || new Date(c.expires_at) < new Date()).length > 0 && (
              <ExpandableSection
                icon={<DollarSign size={16} className="text-muted-foreground" />}
                title="Past Coupons"
              >
                <div className="space-y-1">
                  {coupons.filter((c: any) => c.is_used || new Date(c.expires_at) < new Date()).map((coupon: any) => (
                    <div key={coupon.id} className="flex items-center justify-between p-2 border border-border/20 bg-muted/20 opacity-60 text-xs">
                      <code className="font-mono tracking-widest">{coupon.code} — ${coupon.discount_amount} off</code>
                      <span>{coupon.is_used ? "Used" : "Expired"}</span>
                    </div>
                  ))}
                </div>
              </ExpandableSection>
            )}

            {/* Expandable: Share on Instagram */}
            <ExpandableSection
              icon={<Instagram size={16} className="text-primary" />}
              title="Share on Instagram"
              badge={approvedPosts > 0 ? `${approvedPosts} approved` : undefined}
            >
              <p className="text-sm text-muted-foreground font-light mb-2">
                Share a photo of your results on Instagram and paste the link below to earn a <strong className="text-primary">$5 credit</strong>!
              </p>
              <div className="p-3 border border-amber-500/20 bg-amber-500/5 rounded-sm mb-3">
                <p className="text-xs text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">To be eligible:</strong> You must tag <strong className="text-primary">@paridisebeautyinc_</strong> and include <strong className="text-primary">#Inparadisebeauty</strong> in your post. Don't forget to include your referral code <strong className="text-primary">{referralCode}</strong> in the caption too!
                </p>
              </div>
              <div className="flex gap-2">
                <Input
                  value={igPostInput}
                  onChange={(e) => setIgPostInput(e.target.value)}
                  placeholder="https://www.instagram.com/p/..."
                  className="bg-background border-border/50"
                />
                <Button
                  disabled={igPostSubmitting || !igPostInput.trim()}
                  onClick={async () => {
                    const url = igPostInput.trim();
                    if (!url.includes("instagram.com")) {
                      toast.error("Please enter a valid Instagram post link");
                      return;
                    }
                    if (igPosts.some(p => p.post_url === url)) {
                      toast.error("You've already submitted this post");
                      return;
                    }
                    setIgPostSubmitting(true);
                    try {
                      const shouldAutoApprove = igAutoApprove || profile?.instagram_bypass_approval;
                      const { data: insertedPost, error } = await supabase.from("instagram_posts").insert({
                        user_id: user.id,
                        post_url: url,
                        ...(shouldAutoApprove ? { status: "approved" } : {}),
                      }).select().single();
                      if (error) throw error;

                      if (shouldAutoApprove && insertedPost) {
                        // Auto-generate coupon
                        const code = "IG-" + Math.random().toString(36).substring(2, 8).toUpperCase();
                        const igExpiry = new Date();
                        igExpiry.setMonth(igExpiry.getMonth() + 6);
                        await supabase.from("coupons").insert({
                          user_id: user.id,
                          code,
                          discount_amount: 5,
                          reason: "instagram_share",
                          expires_at: igExpiry.toISOString(),
                        });
                        // Send credit email
                        if (profile?.email) {
                          supabase.functions.invoke("send-credit-email", {
                            body: { email: profile.email, client_name: profile.full_name, coupon_code: code, discount_amount: 5, expires_at: igExpiry.toISOString(), reason: "instagram_share" },
                          }).catch(() => {});
                        }
                        // Refresh coupons
                        const { data: newCoupons } = await supabase.from("coupons").select("*").eq("user_id", user.id).order("created_at", { ascending: false });
                        setCoupons(newCoupons || []);
                        toast.success("Post approved! $5 credit added to your account 🎉");
                      } else {
                        toast.success("Post submitted for review!");
                      }

                      setIgPostInput("");
                      const { data } = await supabase
                        .from("instagram_posts")
                        .select("*")
                        .eq("user_id", user.id)
                        .order("created_at", { ascending: false });
                      setIgPosts((data as any[]) || []);
                    } catch (err: any) {
                      toast.error(err.message || "Failed to submit post");
                    }
                    setIgPostSubmitting(false);
                  }}
                  className="text-xs tracking-widest uppercase shrink-0"
                >
                  {igPostSubmitting ? <Loader2 size={14} className="animate-spin" /> : "Submit"}
                </Button>
              </div>
              {igPosts.length > 0 && (
                <div className="space-y-2 mt-3">
                  {igPosts.map((post) => (
                    <div key={post.id} className="flex items-center justify-between p-2.5 border border-border/30 bg-background text-xs">
                      <a href={post.post_url} target="_blank" rel="noopener noreferrer" className="text-primary underline truncate max-w-[250px]">
                        {post.post_url}
                      </a>
                      <Badge variant={post.status === "approved" ? "default" : post.status === "rejected" ? "destructive" : "secondary"} className="text-[10px]">
                        {post.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-4 text-xs text-muted-foreground mt-2">
                <span>Approved: <strong className="text-primary">{approvedPosts}</strong></span>
                <span>Pending: <strong>{pendingPosts}</strong></span>
              </div>
            </ExpandableSection>

            {/* Expandable: Referral Program */}
            {rewardsEnabled && (
              <ExpandableSection
                icon={<Gift size={16} className="text-primary" />}
                title="Referral Program"
                badge={`${referralCount} referral${referralCount !== 1 ? "s" : ""}`}
              >
                <div className="space-y-4">
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2">Your Referral Code</p>
                    <div className="flex items-center gap-2">
                      <code className="bg-muted px-4 py-2 text-lg font-mono font-bold tracking-widest">{referralCode}</code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(referralCode);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        }}
                      >
                        {copied ? <Check size={14} className="text-primary" /> : <Copy size={14} />}
                      </Button>
                    </div>
                    <div className="mt-2 p-3 border border-primary/20 bg-primary/5 rounded-sm">
                      <p className="text-sm font-medium text-primary mb-1">💰 Earn $5 for every referral!</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        When someone uses your code and completes their first appointment, you automatically receive a <strong className="text-primary">$5 credit</strong>!
                      </p>
                    </div>
                  </div>

                  {/* Automatic $5 per referral - always shown */}
                  <div className="p-3 border border-primary/30 bg-primary/10 text-sm flex items-center justify-between">
                    <span>Every successful referral booking</span>
                    <span className="text-primary font-semibold">$5 credit (automatic)</span>
                  </div>

                  {rewardTiers.length > 0 && (
                    <div className="space-y-2">
                      {rewardTiers.map((tier, i) => (
                        <div key={i} className={`flex items-center justify-between p-3 border text-sm ${
                          referralCount >= tier.referrals_needed
                            ? "border-primary/50 bg-primary/10"
                            : "border-border/30 bg-background"
                        }`}>
                          <span>{tier.referrals_needed} referral{tier.referrals_needed !== 1 ? "s" : ""}</span>
                          <span className={referralCount >= tier.referrals_needed ? "text-primary font-semibold" : "text-muted-foreground"}>
                            {tier.reward_description}
                            {referralCount >= tier.referrals_needed && " ✓"}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="border-t border-border/50 pt-4">
                    <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2">Have a Referral Code?</p>
                    <div className="flex gap-2">
                      <Input
                        value={referralInput}
                        onChange={(e) => setReferralInput(e.target.value.toUpperCase())}
                        placeholder="Enter code"
                        maxLength={8}
                        className="bg-background border-border/50 font-mono tracking-widest uppercase max-w-[200px]"
                      />
                      <Button
                        disabled={referralSubmitting || referralInput.length < 4}
                        onClick={async () => {
                          setReferralSubmitting(true);
                          try {
                            const { data: referrer } = await supabase
                              .from("profiles")
                              .select("user_id")
                              .eq("referral_code", referralInput)
                              .maybeSingle();

                            if (!referrer) {
                              toast.error("Invalid referral code");
                              setReferralSubmitting(false);
                              return;
                            }
                            if (referrer.user_id === user.id) {
                              toast.error("You can't use your own referral code");
                              setReferralSubmitting(false);
                              return;
                            }

                            const { error } = await supabase.from("referral_entries").insert({
                              referrer_user_id: referrer.user_id,
                              referred_user_id: user.id,
                            });

                            if (error) {
                              if (error.code === "23505") {
                                toast.error("You've already used a referral code");
                              } else {
                                toast.error(error.message);
                              }
                            } else {
                              toast.success("Referral code applied! Thank you!");
                              setReferralInput("");
                            }
                          } catch (err: any) {
                            toast.error(err.message);
                          }
                          setReferralSubmitting(false);
                        }}
                        className="text-xs tracking-widest uppercase"
                      >
                        {referralSubmitting ? "Applying…" : "Apply"}
                      </Button>
                    </div>
                  </div>
                </div>
              </ExpandableSection>
            )}
          </div>
          {/* Upcoming */}
          <div className="mb-12">
            <h2 className="font-serif text-2xl font-bold italic mb-6">Upcoming Appointments</h2>
            {loading ? (
              <p className="text-muted-foreground font-light">Loading...</p>
            ) : upcoming.length === 0 ? (
              <p className="text-muted-foreground font-light">No upcoming appointments.</p>
            ) : (
              <div className="space-y-4">
                {upcoming.map((b) => (
                  <BookingCard key={b.id} booking={b} onCancel={handleCancel} onChangeRequest={handleChangeRequest} showCancel locked={isWithin24h(b)} />
                ))}
              </div>
            )}
          </div>

          {/* Past */}
          {past.length > 0 && (
            <div>
              <h2 className="font-serif text-2xl font-bold italic mb-6">Past Appointments</h2>
              <div className="space-y-4">
                {past.map((b) => (
                  <BookingCard key={b.id} booking={b} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />

    </div>
  );
};

const ExpandableSection = ({ icon, title, badge, children }: {
  icon: React.ReactNode;
  title: string;
  badge?: string;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button className="w-full flex items-center justify-between p-3 border border-border/30 bg-muted/10 hover:bg-muted/20 transition-colors text-left">
          <div className="flex items-center gap-2.5">
            {icon}
            <span className="text-sm font-medium">{title}</span>
            {badge && (
              <Badge variant="secondary" className="text-[10px] ml-1">{badge}</Badge>
            )}
          </div>
          {open ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="border border-t-0 border-border/30 p-4">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
};

interface ConfirmedAddon { id: string; name: string; price: number; duration: string }

const BookingCard = ({
  booking,
  onCancel,
  onChangeRequest,
  showCancel,
  locked = false,
}: {
  booking: Booking;
  onCancel?: (id: string) => void;
  onChangeRequest?: (booking: Booking) => void;
  showCancel?: boolean;
  locked?: boolean;
}) => {
  const [confirmedAddons, setConfirmedAddons] = useState<ConfirmedAddon[]>([]);
  const [serviceDescription, setServiceDescription] = useState<string>("");

  useEffect(() => {
    let active = true;
    (async () => {
      const [addonRes, subRes] = await Promise.all([
        supabase
          .from("booking_addons")
          .select("id, name, price, duration")
          .eq("booking_id", booking.id)
          .eq("status", "confirmed")
          .order("created_at", { ascending: true }),
        supabase
          .from("sub_services")
          .select("description")
          .eq("name", booking.service_name)
          .maybeSingle(),
      ]);
      if (!active) return;
      setConfirmedAddons((addonRes.data || []) as ConfirmedAddon[]);
      setServiceDescription((subRes.data?.description as string) || "");
    })();
    return () => { active = false; };
  }, [booking.id, booking.service_name]);

  const addonsTotal = confirmedAddons.reduce((sum, a) => sum + Number(a.price || 0), 0);
  const total = Number(booking.price || 0); // already includes confirmed addons via trigger
  const baseServicePrice = Math.max(0, total - addonsTotal);
  const discount = Number(booking.discount_applied || 0);
  const grandTotal = Math.max(0, total - discount);
  const deposit = Number(booking.deposit_paid_amount || 0);
  const remaining = Math.max(0, grandTotal - deposit);
  const paymentStatus = (booking.payment_status || "").toLowerCase();
  const fullyPaid = paymentStatus === "paid" || (deposit > 0 && remaining <= 0);
  const showPaymentBlock = deposit > 0 || fullyPaid;

  return (
  <div className="bg-card border border-border/50 p-6 flex flex-col gap-4">
    <div className="flex flex-col sm:flex-row sm:items-start gap-4 justify-between">
      <div className="min-w-0">
        <h3 className="font-semibold text-lg">{booking.service_name}</h3>
        <p className="text-muted-foreground text-sm capitalize font-light">{booking.service_category}</p>
        {serviceDescription && (
          <p className="text-muted-foreground text-xs font-light leading-relaxed mt-1.5 max-w-md">
            {serviceDescription}
          </p>
        )}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
        <span className="flex items-center gap-1.5"><CalendarDays size={14} /> {format(new Date(booking.booking_date + "T12:00:00"), "MMM d, yyyy")}</span>
        <span className="flex items-center gap-1.5"><Clock size={14} /> {booking.booking_time}</span>
        <span className="flex items-center gap-1.5"><Clock size={14} /> {booking.duration}</span>
        <span className="flex items-center gap-1.5"><DollarSign size={14} /> ${grandTotal.toFixed(2)}</span>
      </div>
      <div className="flex flex-wrap gap-1.5 self-start">
        <span className={`text-xs font-medium tracking-[0.2em] uppercase px-3 py-1 ${
          booking.status === "confirmed"
            ? "bg-primary/20 text-primary"
            : booking.status === "cancelled"
            ? "bg-destructive/20 text-destructive"
            : "bg-muted text-muted-foreground"
        }`}>
          {booking.status}
        </span>
        {booking.status !== "cancelled" && (fullyPaid ? (
          <span className="text-xs font-medium tracking-[0.2em] uppercase px-3 py-1 bg-emerald-500/15 text-emerald-600">
            Paid in Full
          </span>
        ) : deposit > 0 ? (
          <span className="text-xs font-medium tracking-[0.2em] uppercase px-3 py-1 bg-emerald-500/15 text-emerald-600">
            Deposit Received
          </span>
        ) : null)}
      </div>
    </div>

    {/* Cost breakdown including confirmed add-ons */}
    {(confirmedAddons.length > 0 || discount > 0) && (
      <div className="border-t border-dashed border-border/50 pt-3 space-y-1 text-xs">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">{booking.service_name}</span>
          <span className="text-foreground">${baseServicePrice.toFixed(2)}</span>
        </div>
        {confirmedAddons.map((a) => (
          <div key={a.id} className="flex items-center justify-between">
            <span className="text-muted-foreground">+ {a.name}{a.duration ? ` · ${a.duration}` : ""}</span>
            <span className="text-foreground">${Number(a.price).toFixed(2)}</span>
          </div>
        ))}
        {discount > 0 && (
          <div className="flex items-center justify-between text-green-600">
            <span>Discount</span>
            <span>-${discount.toFixed(2)}</span>
          </div>
        )}
        <div className="flex items-center justify-between pt-1 border-t border-border/30 mt-1">
          <span className="font-medium uppercase tracking-wider text-[10px] text-muted-foreground">Total</span>
          <span className="font-semibold text-foreground">${grandTotal.toFixed(2)}</span>
        </div>
      </div>
    )}

    {/* Deposit / remaining balance breakdown */}
    {showPaymentBlock && (
      <div className="border-t border-dashed border-border/50 pt-3 grid grid-cols-3 gap-3 text-xs">
        <div>
          <p className="text-muted-foreground tracking-wider uppercase text-[10px]">Total</p>
          <p className="font-semibold text-foreground">${grandTotal.toFixed(2)}</p>
        </div>
        <div>
          <p className="text-muted-foreground tracking-wider uppercase text-[10px]">{fullyPaid && deposit >= grandTotal ? "Amount Paid" : "Deposit Paid"}</p>
          <p className="font-semibold text-green-600">${(fullyPaid ? Math.max(deposit, grandTotal) : deposit).toFixed(2)}</p>
        </div>
        <div>
          <p className="text-muted-foreground tracking-wider uppercase text-[10px]">{fullyPaid ? "Status" : "Balance Due"}</p>
          <p className={`font-semibold ${fullyPaid ? "text-green-600" : "text-primary"}`}>
            {fullyPaid ? "Paid in full" : `$${remaining.toFixed(2)}`}
          </p>
        </div>
      </div>
    )}

    {showCancel && onCancel && (
      <>
      <ClientAddonsSection booking={booking} />
      <div className="border-t border-border/50 pt-4 flex flex-col sm:flex-row gap-4 justify-between">
        <AddToCalendarButtons booking={booking} />
        <div className="flex flex-col items-end gap-2 self-start">
          {locked && (
            <p className="text-[11px] text-muted-foreground font-light tracking-wide max-w-[260px] text-right">
              Changes & cancellations close 24 hours before your appointment.
            </p>
          )}
          <div className="flex gap-2">
          {/* Change Appointment */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={locked}
                className="rounded-none border-primary/40 text-primary hover:bg-primary/10 hover:border-primary tracking-[0.2em] uppercase text-xs font-medium disabled:opacity-50 disabled:pointer-events-none"
              >
                <RefreshCw size={12} className="mr-1.5" />
                Change
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-card border-border/50">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-serif text-2xl font-bold italic">
                  Change Appointment?
                </AlertDialogTitle>
                <AlertDialogDescription className="text-muted-foreground font-light leading-relaxed">
                  You're requesting to change your <span className="font-medium text-foreground">{booking.service_name}</span> appointment
                  on <span className="font-medium text-foreground">{format(new Date(booking.booking_date + "T12:00:00"), "MMM d, yyyy")}</span> at <span className="font-medium text-foreground">{booking.booking_time}</span>.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="bg-primary/10 border border-primary/20 p-4 my-2">
                <p className="text-sm font-medium text-primary tracking-wide">
                  ℹ Your new appointment will need to be confirmed by admin.
                </p>
                <p className="text-xs text-muted-foreground font-light mt-1">
                  You'll be taken to the booking page to select a new date and time. Your current appointment will be cancelled once the change is submitted.
                </p>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-none border-border/50 tracking-[0.2em] uppercase text-xs font-medium">
                  Keep Current
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => onChangeRequest?.(booking)}
                  className="rounded-none tracking-[0.2em] uppercase text-xs font-medium"
                >
                  Continue to Rebook
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Cancel Appointment */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={locked}
                className="rounded-none border-destructive/40 text-destructive hover:bg-destructive/10 hover:border-destructive tracking-[0.2em] uppercase text-xs font-medium disabled:opacity-50 disabled:pointer-events-none"
              >
                Cancel
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-card border-border/50">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-serif text-2xl font-bold italic">
                  Cancel Appointment?
                </AlertDialogTitle>
                <AlertDialogDescription className="text-muted-foreground font-light leading-relaxed">
                  Are you sure you want to cancel your <span className="font-medium text-foreground">{booking.service_name}</span> appointment
                  on <span className="font-medium text-foreground">{format(new Date(booking.booking_date + "T12:00:00"), "MMM d, yyyy")}</span> at <span className="font-medium text-foreground">{booking.booking_time}</span>?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="bg-destructive/10 border border-destructive/20 p-4 my-2">
                <p className="text-sm font-medium text-destructive tracking-wide">
                  ⚠ Your deposit will be forfeited and is non-refundable.
                </p>
                <p className="text-xs text-muted-foreground font-light mt-1">
                  Cancellations within 24 hours of your appointment may result in additional charges.
                </p>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-none border-border/50 tracking-[0.2em] uppercase text-xs font-medium">
                  Keep Appointment
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => onCancel(booking.id)}
                  className="rounded-none bg-destructive hover:bg-destructive/90 tracking-[0.2em] uppercase text-xs font-medium"
                >
                  Yes, Cancel
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          </div>
        </div>
      </div>
      </>
    )}
  </div>
  );
};

export default Account;